import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useEventListener, useMemoizedFn, useMount } from 'ahooks';
import { Button, Modal, ModalProps, Space, Spin } from 'antd';
import { useAuthorization } from 'components/Authorization';
import DecorateTitle from 'components/DecorateTitle';
import { isBoolean, isString, noop } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { getLeYanBotHost } from 'services/api/leyanBotHost';
import chatttSDK, { canNetScan } from 'services/chattt';
import { useOnline } from 'hooks/useOnline';
import netErrorImg from '../assets/netError.png';
import { useTransAppProductName } from 'hooks/useTrans';
import { netScanResultCache } from 'services/cache';
import isPromise from 'utils/promiseHelpers';
import { isInCN } from 'utils/is';
import { formatLoggerData, logger } from 'services/logger';

const antIcon = <LoadingOutlined style={{ fontSize: 14 }} spin />;

export interface NetScanModalProps extends ModalProps {
  /**
   * @default manual; - manual: 手动， message: 监听postMessage
   */
  trigger?: 'message' | 'manual';
}

const LastScan = styled.p`
  margin-top: 8px;
  font-size: 12px;
  color: #84868c;
`;
const Title = styled(DecorateTitle)`
  left: 0;
  margin-bottom: 5px;

  &:not(:first-child) {
    margin-top: 18px;
  }
`;
const Wrapper = styled.div`
  display: inline-block;
  padding: 3px 8px;
  background-color: #f0f2f5;
  border-radius: 2px;
  color: #515359;
`;
const List = styled.ul`
  padding-left: 30px;
  color: #515359;
  & > li {
    margin-bottom: 8px;
  }
`;
const ListItem = styled.div`
  display: inline-flex;
  min-width: 300px;
  align-items: center;
  justify-content: space-between;
`;
const NetErrorWrap = styled(Space)`
  width: 100%;
  padding: 20px 0;
`;
const NetErrorImg = styled.img`
  width: 120px;
  height: 120px;
`;

const SCAN_CONFIG = {
  repeat: 6,
  size: 64,
  timeout: 5,
};

export const uploadNetScanResult = (result?: any) => {
  let postResult = result;
  try {
    if (postResult) {
      netScanResultCache.set(postResult);
    } else {
      postResult = netScanResultCache.get();
    }
    if (postResult) {
      logger.info(`[NetScanModal] uploadNetScanResult, ${formatLoggerData(result)}`);
    }
  } catch (err) {
    //
  }
};

const getGatewayHost = () => {
  const url = getLeYanBotHost();
  const urlObj = new URL(url);
  return urlObj.hostname;
};

const getLeyanService = (api: string) => {
  const bot = getLeYanBotHost();
  return `${bot}/${api}`;
};

const SuccessIcon = memo(() => (
  <CheckCircleFilled style={{ fontSize: 14, color: '#00BF69', marginRight: 4 }} />
));
const FailedIcon = memo(() => <CloseCircleFilled style={{ fontSize: 14, color: '#F05B5B' }} />);

interface TestListItemProps {
  title: string;
  hosts: string[] | string;
}

const NetTestListItem = forwardRef((props: TestListItemProps, ref: any) => {
  const { title, hosts } = props;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ isSuccess: boolean; times: string } | null>(null);

  useImperativeHandle(ref, () => ({
    start,
  }));

  const start = useMemoizedFn(() => {
    const testHosts = isString(hosts) ? [hosts] : hosts;
    setLoading(true);
    return Promise.all(testHosts.map((host) => chatttSDK?.netScan?.scan(host, SCAN_CONFIG)))
      .then((res) => {
        const isSuccess = res.every((r) => r?.status === 'online');
        const times = res?.[0]?.res_avg || '';

        setResult({
          isSuccess,
          times,
        });
        return res;
      })
      .catch((err) => {
        setResult({
          isSuccess: false,
          times: '',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const renderResult = useMemo(() => {
    if (result && isBoolean(result?.isSuccess)) {
      if (result.isSuccess) {
        return (
          <>
            <SuccessIcon />
            {result.times}
          </>
        );
      }
      return <FailedIcon />;
    }

    return '-';
  }, [result]);

  return (
    <li>
      <ListItem>
        <span>{title}</span>
        <Wrapper>{loading ? <Spin indicator={antIcon} /> : renderResult}</Wrapper>
      </ListItem>
    </li>
  );
});

interface LeyanServiceTestListItemProps {
  title: string;
  service: string;
  token?: string | null;
}

const LeyanServiceTestListItem = forwardRef((props: LeyanServiceTestListItemProps, ref: any) => {
  const { title, service, token } = props;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ isSuccess: boolean; times: string } | null>(null);

  useImperativeHandle(ref, () => ({
    start,
  }));

  const start = useMemoizedFn(() => {
    setLoading(true);
    let options: any;
    if (token) {
      options = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return chatttSDK?.netScan
      ?.httpGetTiming(service, options)
      .then((result) => {
        if (result.statusCode === 200) {
          setResult({
            isSuccess: true,
            times: result?.timings?.phases?.total,
          });
        } else {
          setResult({
            isSuccess: false,
            times: '',
          });
        }
        return {
          service,
          ...result,
        };
      })
      .catch((err) => {
        setResult({
          isSuccess: false,
          times: '',
        });
        return {
          service,
          errorMsg: err?.message,
        };
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const renderResult = useMemo(() => {
    if (result && isBoolean(result?.isSuccess)) {
      if (result.isSuccess) {
        return (
          <>
            <SuccessIcon />
            {result.times}ms
          </>
        );
      }
      return <FailedIcon />;
    }

    return '-';
  }, [result]);

  return (
    <li>
      <ListItem>
        <span>{title}</span>
        <Wrapper>{loading ? <Spin indicator={antIcon} /> : renderResult}</Wrapper>
      </ListItem>
    </li>
  );
});

export const NetScan = ({ trigger = 'manual' }: Pick<NetScanModalProps, 'trigger'>) => {
  const [lastTime, setLastTime] = useState<string | null>(null);
  const [ipLoading, setIpLoading] = useState(false);
  const [ip, setIp] = useState<string>('-');
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [online, setOnline] = useState(true);
  const onlineAtom = useOnline();

  const [scanLoading, setScanLoading] = useState(false);
  const { token } = useAuthorization<Oversea.Token.Payload>();

  // 网络服务
  const leyanNetRef = useRef<any>();
  const neteaseNetRef = useRef<any>();
  const baiduNetRef = useRef<any>();
  const googleNetRef = useRef<any>();
  const facebookNetRef = useRef<any>();
  const aiNetRef = useRef<any>();
  // 乐聊服务
  const storeRef = useRef<any>();
  const itemsRef = useRef<any>();
  const convRef = useRef<any>();
  const orderRef = useRef<any>();

  const updateLastTime = useMemoizedFn(() => {
    // 兼容不同版本 sdk
    const res = chatttSDK?.netScan?.getLastScanTime();
    if (isPromise(res)) {
      // @ts-ignore
      res.then((lastTime) => {
        if (lastTime) {
          setLastTime(dayjs(lastTime).format('YYYY-MM-DD HH:mm:ss'));
        }
      });
    } else {
      if (res) {
        setLastTime(dayjs(res).format('YYYY-MM-DD HH:mm:ss'));
      }
    }
  });

  useMount(() => {
    updateLastTime();
    setOnline(onlineAtom);
  });

  useEventListener(
    'message',
    trigger === 'message'
      ? (e) => {
          if (!canNetScan) {
            return;
          }
          if (e?.data?.cmd === 'start_net_scan') {
            startScan();
          }
        }
      : noop,
  );

  const startScanIp = useMemoizedFn(() => {
    setIpLoading(true);
    return chatttSDK?.netScan
      ?.getMyIpAddress()
      .then((res) => {
        if (res) {
          const { countryCode, ip } = res;
          setIp(`${countryCode} ${ip}`);
        }
        return res;
      })
      .finally(() => {
        setIpLoading(false);
      });
  });

  const isCN = isInCN();
  const startScan = useMemoizedFn(() => {
    setScanLoading(true);
    const skipCheckPromise = Promise.resolve({});
    Promise.all([
      startScanIp(),
      leyanNetRef?.current?.start(),
      neteaseNetRef?.current?.start(),
      baiduNetRef?.current?.start(),
      isCN ? skipCheckPromise : googleNetRef?.current?.start(),
      isCN ? skipCheckPromise : facebookNetRef?.current?.start(),
      storeRef?.current?.start(),
      itemsRef?.current?.start(),
      convRef?.current?.start(),
      orderRef?.current?.start(),
      aiNetRef?.current?.start(),
    ])
      .then((result) => {
        uploadNetScanResult(result);
        updateLastTime();
      })
      .finally(() => {
        setScanLoading(false);
      });
  });

  return (
    <>
      {online ? (
        <>
          <Button type="primary" onClick={startScan} loading={scanLoading}>
            {t('ViewSystem.system.net.scan.btn.title')}
          </Button>
          {lastTime && (
            <LastScan>{t('ViewSystem.system.net.scan.last.times', { times: lastTime })}</LastScan>
          )}
          <Title>{t('ViewSystem.system.net.scan.title.ip')}</Title>
          <Wrapper>{ipLoading ? <Spin indicator={antIcon} /> : ip}</Wrapper>
          <Title>{t('ViewSystem.system.net.scan.title.network')}</Title>
          <List>
            <NetTestListItem
              ref={leyanNetRef}
              title={t('ViewSystem.system.net.scan.title.li.1')}
              hosts={getGatewayHost()}
            />
            <NetTestListItem
              ref={neteaseNetRef}
              title={t('ViewSystem.system.net.scan.title.li.2')}
              hosts="netease.im"
            />
            <NetTestListItem
              ref={baiduNetRef}
              title={t('ViewSystem.system.net.scan.title.li.3')}
              hosts="baidu.com"
            />
            {isCN ? null : (
              <NetTestListItem
                ref={googleNetRef}
                title={t('ViewSystem.system.net.scan.title.li.8')}
                hosts="google.com"
              />
            )}
            {isCN ? null : (
              <NetTestListItem
                ref={facebookNetRef}
                title={t('ViewSystem.system.net.scan.title.li.9')}
                hosts="facebook.com"
              />
            )}
          </List>
          <Title>{appProductNameT('ViewSystem.system.net.scan.title.leliao')}</Title>
          <List>
            <LeyanServiceTestListItem
              ref={storeRef}
              title={t('ViewSystem.system.net.scan.title.li.4')}
              service={getLeyanService('overseaim-store-home/health')}
              token={token}
            />
            {!token ? null : (
              <>
                <LeyanServiceTestListItem
                  ref={convRef}
                  title={t('ViewSystem.system.net.scan.title.li.5')}
                  service={getLeyanService('oversea-door/oversea-conversation/v1/healthCheck')}
                  token={token}
                />
                <LeyanServiceTestListItem
                  ref={itemsRef}
                  title={t('ViewSystem.system.net.scan.title.li.6')}
                  service={getLeyanService('oversea-door/oversea-item/v1/health')}
                  token={token}
                />
                <LeyanServiceTestListItem
                  ref={orderRef}
                  title={t('ViewSystem.system.net.scan.title.li.7')}
                  service={getLeyanService('oversea-door/trade/v1/health')}
                  token={token}
                />
              </>
            )}
            <NetTestListItem
              ref={aiNetRef}
              title={t('ViewSystem.system.net.scan.title.li.10')}
              hosts="unipolar-neuron.zhisuitech.com"
            />
          </List>
        </>
      ) : (
        <NetErrorWrap direction="vertical" align="center">
          <NetErrorImg src={netErrorImg} alt="net error" />
          <LastScan>{t('translation.网络无法连接，请使用健康诊断检查下你的网络')}</LastScan>
        </NetErrorWrap>
      )}
    </>
  );
};

const NetScanModal = ({ trigger, ...props }: NetScanModalProps) => {
  const { t } = useTranslate();

  return (
    <Modal title={t('ViewSystem.system.net.scan.title')} footer={null} {...props}>
      <NetScan trigger={trigger} />
    </Modal>
  );
};

export default NetScanModal;
